import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, MapPin, Package, CheckCircle2, XCircle, Clock, PauseCircle, RefreshCw, ExternalLink, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';

const STATUS_CONFIG = {
  open:   { label: 'Open',   bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500'   },
  won:    { label: 'Won',    bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500'  },
  lost:   { label: 'Lost',   bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500'    },
  parked: { label: 'Parked', bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500'  },
};

const SESSION_STATUS = {
  open:   { label: 'Open',   ring: 'ring-emerald-400', headerBg: 'bg-emerald-700' },
  closed: { label: 'Closed', ring: 'ring-gray-300',    headerBg: 'bg-gray-500'    },
};

const LeadIcon = ({ status }) => {
  if (status === 'won')    return <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />;
  if (status === 'lost')   return <XCircle      className="w-3.5 h-3.5 text-red-500"   />;
  if (status === 'parked') return <PauseCircle  className="w-3.5 h-3.5 text-amber-500" />;
  return                          <Clock        className="w-3.5 h-3.5 text-blue-500"  />;
};

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
const fmtVisitDate = (d) => {
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const ClientTimeline = () => {
  const { phone } = useParams();
  const navigate  = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  const handleBack = () => navigate(-1);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/crm/clients/${encodeURIComponent(phone)}/timeline`);
        setData(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [phone]);

  if (loading) return (
    <div className="h-screen flex flex-col bg-gray-50">
      <div className="bg-[#075E54] text-white px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={handleBack} className="text-white hover:bg-emerald-700 p-2 flex-shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="font-bold text-lg">Client Timeline</span>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    </div>
  );

  if (!data || !data.sessions.length) return (
    <div className="h-screen flex flex-col bg-gray-50">
      <div className="bg-[#075E54] text-white px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={handleBack} className="text-white hover:bg-emerald-700 p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="font-bold text-lg">Client Timeline</span>
        </div>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
        <Package className="w-16 h-16 mb-4 opacity-40" />
        <p className="font-medium">No sessions found for {phone}</p>
      </div>
    </div>
  );

  const { client_name, client_phone, sessions, stats } = data;

  // Compute Won / Lost / Parked deal-value totals from all leads across all sessions
  const allLeads = sessions.flatMap(s => s.leads || []);
  const wonTotal    = allLeads.filter(l => l.status === 'won').reduce((s, l) => s + (l.estimated_value || 0), 0);
  const lostTotal   = allLeads.filter(l => l.status === 'lost').reduce((s, l) => s + (l.estimated_value || 0), 0);
  const parkedTotal = allLeads.filter(l => l.status === 'parked').reduce((s, l) => s + (l.estimated_value || 0), 0);
  const hasSummary  = wonTotal > 0 || lostTotal > 0 || parkedTotal > 0;

  const fmtAmt = (n) => n >= 100000
    ? `₹${(n / 100000).toFixed(1)}L`
    : n >= 1000
      ? `₹${(n / 1000).toFixed(1)}K`
      : `₹${Math.round(n).toLocaleString('en-IN')}`;

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 shadow-md flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={handleBack} className="text-white hover:bg-emerald-700 p-2 flex-shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold truncate" data-testid="client-timeline-name">{client_name}</h1>
            <p className="text-xs text-emerald-200 flex items-center gap-1">
              <Phone className="w-3 h-3" />{client_phone}
            </p>
          </div>
          <button
            onClick={() => navigate('/clients')}
            className="flex items-center gap-1 text-xs text-emerald-200 hover:text-white transition-colors flex-shrink-0"
            data-testid="go-to-clients-search-btn"
            title="Search other clients"
          >
            <Search className="w-4 h-4" />
            <span className="hidden sm:inline">All Clients</span>
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b px-4 py-3 flex-shrink-0">
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-800" data-testid="stat-sessions">{stats.total_sessions}</p>
            <p className="text-xs text-gray-400">Sessions</p>
          </div>
          <div className="w-px h-8 bg-gray-200" />
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-600" data-testid="stat-visits">{stats.total_visits}</p>
            <p className="text-xs text-gray-400">Total Visits</p>
          </div>
          <div className="w-px h-8 bg-gray-200" />
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600" data-testid="stat-open-leads">{stats.open_leads}</p>
            <p className="text-xs text-gray-400">Open Leads</p>
          </div>
        </div>
      </div>

      {/* Deal Value Summary — Won / Lost / Parked */}
      {hasSummary && (
        <div className="bg-white border-b flex-shrink-0" data-testid="deal-summary-panel">
          <div className="grid grid-cols-3 divide-x">
            <div className="flex flex-col items-center justify-center py-3 px-2 gap-0.5" data-testid="deal-won-total">
              <CheckCircle2 className="w-4 h-4 text-green-500 mb-0.5" />
              <p className="text-base font-bold text-green-600 leading-none">{fmtAmt(wonTotal)}</p>
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">Won</p>
            </div>
            <div className="flex flex-col items-center justify-center py-3 px-2 gap-0.5" data-testid="deal-lost-total">
              <XCircle className="w-4 h-4 text-red-400 mb-0.5" />
              <p className="text-base font-bold text-red-500 leading-none">{fmtAmt(lostTotal)}</p>
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">Lost</p>
            </div>
            <div className="flex flex-col items-center justify-center py-3 px-2 gap-0.5" data-testid="deal-parked-total">
              <PauseCircle className="w-4 h-4 text-amber-400 mb-0.5" />
              <p className="text-base font-bold text-amber-500 leading-none">{fmtAmt(parkedTotal)}</p>
              <p className="text-[11px] text-gray-400 uppercase tracking-wide">Parked</p>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto p-4" data-testid="client-timeline">
        <div className="max-w-2xl mx-auto">
          {/* Timeline vertical line */}
          <div className="relative">
            {/* Left connecting line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 z-0" />

            {sessions.map((session, idx) => {
              const isLast = idx === sessions.length - 1;
              const sStatus = SESSION_STATUS[session.status] || SESSION_STATUS.closed;
              const visitCount = (session.visits?.length || 0) + 1;

              // Build unified timeline items for this session
              const timelineItems = [];
              (session.visits || []).forEach(v => {
                timelineItems.push({ type: 'visit', data: v, time: new Date(v.recorded_at) });
              });
              timelineItems.sort((a, b) => a.time - b.time);

              return (
                <div key={session._id} className="relative pl-12 mb-6" data-testid={`timeline-session-${session.session_number}`}>
                  {/* Circle dot on timeline */}
                  <div className={`absolute left-2.5 top-3 w-3 h-3 rounded-full border-2 border-white z-10 ${session.status === 'open' ? 'bg-emerald-500' : 'bg-gray-400'}`} />

                  {/* Session Card */}
                  <div className={`rounded-xl border-2 overflow-hidden shadow-sm ${session.status === 'open' ? 'border-emerald-200' : 'border-gray-200'}`}>
                    {/* Session Header */}
                    <div className={`px-4 py-3 ${sStatus.headerBg} text-white`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-sm">{session.session_number}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${session.status === 'open' ? 'bg-emerald-400/30' : 'bg-white/20'}`}>
                              {session.status}
                            </span>
                          </div>
                          <p className="text-xs text-white/80 mt-0.5">
                            {fmt(session.created_at)} · {session.assigned_salesman_name}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 text-white/90 text-xs">
                            <MapPin className="w-3 h-3" />
                            <span>{visitCount} {visitCount === 1 ? 'visit' : 'visits'}</span>
                          </div>
                          <button
                            onClick={() => navigate(`/lead-sessions/${session._id}`)}
                            className="p-1 rounded hover:bg-white/20 transition-colors"
                            data-testid={`open-session-${session.session_number}`}
                            title="Open session"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Leads */}
                    {session.leads?.length > 0 && (
                      <div className="px-4 py-3 bg-white border-b border-gray-100">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Leads</p>
                        <div className="space-y-1.5">
                          {session.leads.map(lead => {
                            const sc = STATUS_CONFIG[lead.status] || STATUS_CONFIG.open;
                            return (
                              <div key={lead._id} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <LeadIcon status={lead.status} />
                                  <span className="text-sm text-gray-800 font-medium">{lead.product_name}</span>
                                  {lead.main_group && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-medium">{lead.main_group}</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {lead.estimated_value > 0 && (
                                    <span className="text-xs text-gray-500">₹{Math.round(lead.estimated_value).toLocaleString('en-IN')}</span>
                                  )}
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${sc.bg} ${sc.text}`}>{sc.label}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Visit log within session */}
                    {timelineItems.length > 0 && (
                      <div className="px-4 py-3 bg-gray-50">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Return Visits</p>
                        <div className="space-y-2">
                          {timelineItems.map((item) => {
                            const v = item.data;
                            return (
                              <div key={v.visit_number} className="flex items-start gap-2" data-testid={`visit-${session.session_number}-${v.visit_number}`}>
                                <div className="flex-shrink-0 mt-0.5">
                                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                                    <MapPin className="w-3 h-3 text-emerald-600" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-emerald-700">Visit #{v.visit_number}</span>
                                    <span className="text-[10px] text-gray-400">{fmtVisitDate(v.date)}</span>
                                    <span className="text-[10px] text-gray-400">· {v.salesman_name}</span>
                                  </div>
                                  {v.summary && (
                                    <p className="text-xs text-gray-600 mt-0.5 italic">"{v.summary}"</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Empty state for no visits yet */}
                    {timelineItems.length === 0 && session.leads?.length === 0 && (
                      <div className="px-4 py-3 bg-white text-center">
                        <p className="text-xs text-gray-400">No leads or visits recorded yet</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* End of timeline marker */}
            <div className="relative pl-12">
              <div className="absolute left-2.5 top-2 w-3 h-3 rounded-full bg-gray-300 border-2 border-white z-10" />
              <p className="text-xs text-gray-400 py-2">Beginning of client history</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientTimeline;
